/* ==========================================================================
   Catalogue — sticky category bar

   The tabs are generated from the `catalogue-list` sections actually present on
   the page (each one exposes `id` + `data-bpc-category`), so adding, removing
   or reordering a category in the theme editor never leaves the bar out of
   sync. Placeholder tabs configured on the section are appended after them.

   Also publishes the bar's height into `--bpc-tabs-h`; catalogue-list.css uses
   it for `scroll-margin-top` so an anchored category is not hidden behind it.
   ========================================================================== */

(function () {
  'use strict';

  function build(bar) {
    var inner = bar.querySelector('.bpc-tabs-inner');
    if (!inner) return;

    var categories = document.querySelectorAll('.bpc[data-bpc-category]');
    var frag = document.createDocumentFragment();
    var count = 0;

    categories.forEach(function (section) {
      var label = section.getAttribute('data-bpc-category');
      if (!label || !section.id) return;

      var link = document.createElement('a');
      link.className = 'bpc-tab-btn';
      link.href = '#' + section.id;
      link.textContent = label;
      link.setAttribute('data-bpc-target', section.id);
      frag.appendChild(link);
      count++;
    });

    // Categories that exist commercially but have no products loaded yet.
    var extras = (bar.getAttribute('data-bpc-placeholders') || '')
      .split('|')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);

    extras.forEach(function (label) {
      var span = document.createElement('span');
      span.className = 'bpc-tab-btn';
      span.setAttribute('aria-disabled', 'true');
      span.setAttribute('title', 'Coming soon');
      span.textContent = label;
      frag.appendChild(span);
    });

    if (!count && !extras.length) return;

    // The theme editor re-runs this on every section change; drop the previous
    // pass's listeners so they don't stack up.
    if (bar.__bpcTeardown) bar.__bpcTeardown();

    inner.textContent = '';
    inner.appendChild(frag);
    bar.setAttribute('data-ready', 'true');

    publishHeight(bar);
    bar.__bpcTeardown = spy(bar);
  }

  function publishHeight(bar) {
    var apply = function () {
      var h = Math.ceil(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--bpc-tabs-h', h + 'px');
    };

    apply();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(apply).observe(bar, { box: 'border-box' });
    } else {
      window.addEventListener('resize', apply);
    }

    // Webfonts land after first paint and change the bar's line box by a couple
    // of pixels, which would otherwise leave the anchor offset slightly short.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(apply);
    }
    window.addEventListener('load', apply);
  }

  function spy(bar) {
    var inner = bar.querySelector('.bpc-tabs-inner');
    var links = Array.prototype.slice.call(bar.querySelectorAll('[data-bpc-target]'));
    if (!links.length) return;

    var current = null;
    // A click is an explicit choice: hold it while the smooth scroll is still
    // travelling, otherwise the intermediate positions would drag the
    // highlight through every category on the way.
    var holdUntil = 0;

    function reveal(link) {
      // Scroll the horizontal strip only. scrollIntoView() would also move the
      // page, which fights the scroll it is reacting to.
      if (!inner || inner.scrollWidth <= inner.clientWidth) return;
      var left = link.offsetLeft;
      var right = left + link.offsetWidth;
      if (left < inner.scrollLeft) {
        inner.scrollLeft = Math.max(0, left - 16);
      } else if (right > inner.scrollLeft + inner.clientWidth) {
        inner.scrollLeft = right - inner.clientWidth + 16;
      }
    }

    // function activate(link) {
    //   if (link === current) return;
    //   current = link;
    //   links.forEach(function (other) {
    //     other.classList.toggle('is-active', other === link);
    //   });
    //   reveal(link);
    // }

    function update() {
      if (Date.now() < holdUntil) return;

      var line = bar.getBoundingClientRect().height + 8;
      var found = links[0];

      // The current category is the last one whose top has passed under the bar.
      links.forEach(function (link) {
        var section = document.getElementById(link.getAttribute('data-bpc-target'));
        if (section && section.getBoundingClientRect().top <= line) found = link;
      });

      // activate(found);
    }

    // Mark the clicked tab straight away rather than waiting for the scroll to
    // report back — the highlight must not depend on scroll events arriving.
    links.forEach(function (link) {
      link.addEventListener('click', function () {
        holdUntil = Date.now() + 900;
        // activate(link);
      });
    });

    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        update();
      }, 100);
    }

    update();

    // `scroll` does not bubble, so a capturing listener on the document is what
    // catches it when the page scrolls inside a wrapper rather than the window.
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return function teardown() {
      window.removeEventListener('scroll', onScroll, { passive: true });
      document.removeEventListener('scroll', onScroll, { passive: true, capture: true });
      window.removeEventListener('resize', onScroll, { passive: true });
    };
  }

  function init() {
    document.querySelectorAll('.bpc-tabs').forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan when a section is added, removed or reordered.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  document.addEventListener('shopify:section:reorder', init);
})();
